import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useAuth0 } from '@auth0/auth0-react';
import { useStripeCheckout } from '../../hooks/useStripeCheckout';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useUser } from '../../contexts/UserContext';
import { getSmartReturnTo } from '../../utils/auth';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { getPromo } from '../../config/promos';

const PRICING = {
  monthly: { price: 42, display: '$42', period: 'mo' },
  annual: { price: 199, display: '$199', period: 'yr' }
};

const FEATURES = [
  'Tap to Bet on FanDuel & DraftKings',
  'Adaptive odds — updated more frequently near game time',
  'All edges for every sport in season',
  'Player props alongside game lines'
];

/**
 * PremiumFeatureDialog - Mobile-first upgrade dialog
 * Shows as bottom sheet on mobile, centered modal on desktop
 *
 * @param {string} mode - Dialog mode: 'trial' (free trial CTA), 'expired' (resubscribe CTA), or 'upgrade' (default, generic upgrade)
 * @param {string} promoCode - Optional promo code to pre-apply at checkout (e.g., 'WINBACK24')
 * @param {string} promoLabel - Optional label to show when promo is applied (e.g., '20% off')
 */
export const PremiumFeatureDialog = ({ open, onClose, featureName = 'Premium Features', analyticsKey, mode = 'upgrade', promoCode = null, promoLabel = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { handleSubscribe, loading } = useStripeCheckout();
  const { trackSubscribeClick, trackPlanToggle, trackPremiumFeatureBlocked, trackUpgradePromptViewed, trackUpgradePromptClicked } = useAnalytics();
  const { status } = useUser();
  const [billingInterval, setBillingInterval] = useState('annual');
  const offer = getPromo(promoCode);
  const interval = offer?.interval || billingInterval;

  // A/B challenger: proof-led CC wall. Off by default (control) until the
  // cc_wall_proof flag is created in PostHog. The trial is 7 days from checkout.
  const ccWallProof = useFeatureFlag('cc_wall_proof', false);

  // Use analyticsKey for PostHog `feature_name` to keep historical funnels stable
  // across brand-language renames (e.g., 'All Picks' -> 'All Edges' display change
  // but feature_name analytics property stays 'all_picks'). Falls back to featureName.
  const eventFeatureKey = analyticsKey || featureName;
  const noChargeDate = useMemo(() => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), []);
  
  // Track when dialog opens (premium gate hit)
  useEffect(() => {
    if (open) {
      // CC-wall shown — the upgrade_prompt_viewed funnel step. prompt_type=mode
      // ('trial' = the trial credit-card wall) distinguishes it from other prompts.
      trackUpgradePromptViewed(
        mode,
        'premium_feature_dialog',
        {
          feature_name: eventFeatureKey,
          sport: extractSportFromPath(),
          page_path: window.location.pathname,
          user_tier: status,
          cc_wall_variant: ccWallProof ? 'proof' : 'control'
        }
      );
      trackPremiumFeatureBlocked(
        eventFeatureKey,
        'dialog_shown',
        {
          sport: extractSportFromPath(),
          page_path: window.location.pathname,
          user_tier: status
        }
      );
    }
  }, [open, eventFeatureKey, mode, status, ccWallProof, trackUpgradePromptViewed, trackPremiumFeatureBlocked]);
  
  // Helper to extract sport from pathname
  const extractSportFromPath = () => {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/nba')) return 'nba';
    if (path.includes('/nfl')) return 'nfl';
    if (path.includes('/mlb')) return 'mlb';
    if (path.includes('/nhl')) return 'nhl';
    if (path.includes('/ncaaf')) return 'ncaaf';
    if (path.includes('/mens-cbb') || path.includes('/menscbb')) return 'menscbb';
    return 'unknown';
  };

  const handleBillingChange = (event, newValue) => {
    if (newValue !== null) {
      trackPlanToggle(billingInterval, newValue);
      setBillingInterval(newValue);
    }
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: getSmartReturnTo() }
      });
      return;
    }

    // Track upgrade prompt click
    trackUpgradePromptClicked(
      'dialog',
      'premium_feature_dialog',
      {
        feature_name: eventFeatureKey,
        sport: extractSportFromPath(),
        page_path: window.location.pathname,
        user_tier: status,
        destination: '/edgeboard',
        billing_cycle: interval
      }
    );

    trackSubscribeClick('Premium', interval === 'annual');
    // Pass withTrial=true when in trial mode, and promoCode if provided
    const withTrial = mode === 'trial';
    await handleSubscribe('Premium', interval === 'annual', null, withTrial, promoCode);
  };


  // Mobile: Bottom Sheet style
  const mobileStyles = {
    '& .MuiDialog-container': {
      alignItems: 'flex-end'
    },
    '& .MuiDialog-paper': {
      margin: 0,
      maxHeight: '90vh',
      borderRadius: '16px 16px 0 0',
      width: '100%'
    }
  };

  // Desktop: Centered modal
  const desktopStyles = {
    '& .MuiDialog-paper': {
      borderRadius: 3,
      maxWidth: 500
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={false}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: isMobile ? mobileStyles : desktopStyles
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            {mode === 'expired' ? 'Your Trial Has Ended' : mode === 'trial' ? 'Start Your Free Trial' : `Unlock ${featureName}`}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {mode === 'expired' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Subscribe to regain access to all premium features.
          </Typography>
        )}
        {mode === 'trial' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Try Premium free for 7 days. Cancel anytime.
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* The cc_wall_proof variant used to lead with the TrackRecord stats.
            No record on Upgrade dialogs (#283); the flag still drives the
            no-charge-date line below. */}
        {/* Promo Banner */}
        {promoCode && promoLabel && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 1.5, 
              bgcolor: 'success.main', 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LocalOfferIcon sx={{ color: 'white', fontSize: '1.25rem' }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
              {promoLabel} applied at checkout!
            </Typography>
          </Box>
        )}

        {/* Pricing Toggle */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={interval}
            exclusive
            onChange={handleBillingChange}
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: 1.5,
                px: 2,
                border: `1px solid ${theme.palette.primary.main}`,
                '&.Mui-selected': {
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }
              }
            }}
          >
            <ToggleButton value="monthly">
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Monthly
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {mode === 'trial' ? '7 days free, then ' : ''}{offer?.monthlyPrice || PRICING.monthly.display}/{PRICING.monthly.period}
                </Typography>
              </Box>
            </ToggleButton>
            {offer?.interval !== 'monthly' && (
              <ToggleButton value="annual">
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body1" fontWeight={600}>
                      Annual
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {mode === 'trial' ? '7 days free, then ' : ''}{PRICING.annual.display}/{PRICING.annual.period}
                  </Typography>
                </Box>
              </ToggleButton>
            )}
          </ToggleButtonGroup>

        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Features List */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
            Everything you get with Premium:
          </Typography>
          {FEATURES.map((feature, index) => (
            <Box 
              key={index}
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                mb: 1.5
              }}
            >
              <CheckCircleOutlineIcon 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontSize: '1.25rem',
                  mr: 1.5,
                  mt: 0.25
                }} 
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleUpgrade}
          disabled={loading}
          sx={{
            py: 1.5,
            fontWeight: 700,
            fontSize: '1rem'
          }}
        >
          {loading
            ? 'Processing...'
            : !isAuthenticated
              ? 'Login to Continue'
              : mode === 'expired'
                ? 'Subscribe Now'
                : mode === 'trial'
                  ? 'Start Free Trial'
                  : 'Upgrade Now'
          }
        </Button>
        {ccWallProof && mode === 'trial' && (
          <Typography variant="caption" align="center" sx={{ color: 'text.secondary' }}>
            Cancel anytime — no charge until {noChargeDate}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PremiumFeatureDialog;

